import { Injectable } from '@angular/core';
import { BaseAttachmentModel } from '@d-models';
import * as xlsx from 'xlsx';
import { Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import StyledXlsx from 'xlsx-js-style';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

interface ReadSheetOptions {
  startRow?: number;
}

interface OverwriteSheetOptions {
  startRow?: number;
  startCol?: string;
  styles?: {
    [key: string]: object;
  };
}

@Injectable({
  providedIn: 'root',
})
export class XlsxService {
  constructor(private translateService: TranslateService) {}

  readSheet<RowType>(
    file: BaseAttachmentModel | any,
    sheetNo = 0,
    options?: ReadSheetOptions,
  ) {
    return new Observable<XlsxRowType<RowType>[]>((subscriber) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const workbook = xlsx.read(e.target?.result, { type: 'binary' });
        const rows = xlsx.utils.sheet_to_json<XlsxRowType<RowType>>(
          workbook.Sheets[workbook.SheetNames[sheetNo]],
        );

        if (options?.startRow) {
          const data = xlsx.utils.sheet_to_json(
            workbook.Sheets[workbook.SheetNames[sheetNo]],
            { header: 1 },
          );
          const selectedData: any = data.slice(
            options.startRow - 1,
            data.length,
          );
          const selectedDataSheet = xlsx.utils.aoa_to_sheet(selectedData);
          subscriber.next({
            data: xlsx.utils.sheet_to_json(selectedDataSheet),
            header: data.slice(options.startRow - 1, options.startRow)[0],
          } as any);
          subscriber.complete();
        }
        subscriber.next(rows);
        subscriber.complete();
      };
      reader.onerror = (e) => {
        subscriber.error(e);
        subscriber.complete();
      };
      reader.readAsBinaryString(file);
    });
  }

  exportFromRows<T extends Object>(rows: T[], fileName = 'Book1.xlsx') {
    const translatedRows = rows.map((row) =>
      Object.fromEntries(
        Object.entries(row).map(([k, v]) => [
          this.translateService.instant(k),
          v,
        ]),
      ),
    );
    const sheet = xlsx.utils.json_to_sheet(translatedRows);
    const workbook = xlsx.utils.book_new();
    xlsx.utils.book_append_sheet(workbook, sheet, 'Sheet1');
    xlsx.writeFile(workbook, fileName);
  }

  async overwriteWithData(
    file: BaseAttachmentModel | any,
    data: { [columnName: string]: any }[],
    {
      startRow = 1, // 'A'
      startCol = 1,
      newFileName = file.name,
      sheetIndex = 0,
    },
  ) {
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(file);
    const columnsMap = new Map<string, number>();
    const sheet = workbook.worksheets[sheetIndex];
    data.forEach((row, i) => {
      Object.entries(row).forEach(([columnName, cellValue]: [string, any]) => {
        if (columnsMap.get(columnName) === undefined) {
          columnsMap.set(columnName, columnsMap.size);
        }
        const currentColumnIndex = startCol + (columnsMap.get(columnName) || 0);
        const headerRow = sheet.getRow(startRow);
        headerRow.getCell(currentColumnIndex).value = columnName;
        const currentRow = sheet.getRow(startRow + 1 + i);
        currentRow.getCell(currentColumnIndex).value = cellValue;
      });
    });
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), newFileName);
  }
}

export type XlsxRowType<RowType> = RowType & { __rowNum__: number };
